import React, { useState } from 'react';
import { ResponsiveModal } from '../../ui/ResponsiveModal';
import { Copy, CheckCircle, Phone } from 'lucide-react';

interface SignatureShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  signatureLink: string;
  shareMethod: 'copy' | 'whatsapp' | 'email';
  onShareMethodChange: (method: 'copy' | 'whatsapp' | 'email') => void;
  onShare: () => void;
  translations: any;
  signatureLinkCopied: boolean;
}

const SignatureShareModal: React.FC<SignatureShareModalProps> = ({
  isOpen,
  onClose,
  signatureLink,
  shareMethod,
  onShareMethodChange,
  onShare,
  translations: t,
  signatureLinkCopied
}) => {
  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={t.signature?.shareTitle || "Share Signature Link"}
      size="md"
    >
      <div className="space-y-6">
        <p className="text-black/70">{t.signature?.shareDescription || "Share the signature link with your client using one of the following methods:"}</p>
        
        {/* Link display */}
        <div className="bg-gray-50 p-3 rounded-lg flex items-center justify-between">
          <input 
            type="text" 
            value={signatureLink} 
            readOnly 
            className="block w-full px-3 py-2 border-0 focus:ring-0 bg-transparent text-sm text-gray-600 overflow-hidden"
          />
          <button
            onClick={onShare}
            className="flex-shrink-0 ml-2 p-2 rounded-full hover:bg-gray-200 transition-colors"
          >
            {signatureLinkCopied ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <Copy className="h-5 w-5 text-gray-500" />
            )}
          </button>
        </div>
        
        {/* Sharing options */}
        <div className="flex flex-col space-y-4">
          <div className="text-sm font-medium text-gray-700 mb-2">
            {t.signature?.shareVia || "Share via:"}
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <button
              type="button"
              onClick={() => onShareMethodChange('copy')}
              className={`flex flex-col items-center justify-center p-4 rounded-lg border ${
                shareMethod === 'copy' 
                  ? 'border-black bg-black/5' 
                  : 'border-black/10 hover:bg-black/5'
              }`}
            >
              <Copy className="h-6 w-6 mb-2 text-gray-700" />
              <span className="text-sm">
                {t.signature?.copyLink || "Copy Link"}
              </span>
            </button>
            
            <button
              type="button"
              onClick={() => onShareMethodChange('whatsapp')}
              className={`flex flex-col items-center justify-center p-4 rounded-lg border ${
                shareMethod === 'whatsapp' 
                  ? 'border-green-600 bg-green-50' 
                  : 'border-black/10 hover:bg-green-50'
              }`}
            >
              <Phone className="h-6 w-6 mb-2 text-green-600" />
              <span className="text-sm">WhatsApp</span>
            </button>
            
            <button
              type="button"
              onClick={() => onShareMethodChange('email')}
              className={`flex flex-col items-center justify-center p-4 rounded-lg border ${
                shareMethod === 'email' 
                  ? 'border-blue-600 bg-blue-50' 
                  : 'border-black/10 hover:bg-blue-50'
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 mb-2 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="text-sm">Email</span>
            </button>
          </div>
        </div>
        
        {/* Preview of sharing method */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium mb-3">{t.signature?.preview || "Preview"}:</h4>
          
          {shareMethod === 'copy' && (
            <div className="text-sm text-gray-700">
              <p>{t.signature?.copyPreview || "Link will be copied to clipboard for pasting anywhere."}</p>
            </div>
          )}
          
          {shareMethod === 'whatsapp' && (
            <div className="text-sm text-gray-700 space-y-2">
              <p>{t.signature?.whatsappPreview || "Will open a WhatsApp share with this message"}:</p>
              <div className="bg-white p-3 rounded border border-green-100">
                <p className="text-green-800">{t.signature?.whatsappMessage || "Please sign the document at the following link:"}</p>
                <p className="text-blue-600 truncate">{signatureLink}</p>
              </div>
            </div>
          )}
          
          {shareMethod === 'email' && (
            <div className="text-sm text-gray-700 space-y-2">
              <p>{t.signature?.emailPreview || "Will open your email client with"}:</p>
              <div className="bg-white p-3 rounded border border-blue-100 space-y-1">
                <p><span className="font-medium">Subject:</span> {t.signature?.emailSubject || "Document for signature"}</p>
                <p><span className="font-medium">Body:</span></p>
                <p>{t.signature?.emailMessage || "Please sign the document at the following link:"}</p>
                <p className="text-blue-600 truncate">{signatureLink}</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-black glass-card rounded-lg hover:bg-black/5"
          >
            {t.form?.buttons?.cancel || "Cancel"}
          </button>
          <button
            type="button"
            onClick={onShare}
            className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg ${
              shareMethod === 'whatsapp' 
                ? 'bg-green-600 hover:bg-green-700' 
                : shareMethod === 'email'
                  ? 'bg-[#0f6cbd] hover:bg-[#0a5ba8]'
                  : 'bg-black hover:bg-black/80'
            }`}
          >
            {t.signature?.shareNow || "Share Now"}
          </button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default SignatureShareModal;